import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { firestore } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";

/* ----------- UI ----------- */

const Card = styled.section`
  border-radius: 18px;
  border: 1px solid #e5e7eb;
  background: #fff;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const Header = styled.div`
  padding: 16px 18px;
  border-bottom: 1px solid #f1f5f9;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 900;
  color: #111827;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
`;

const FilterBtn = styled.button`
  appearance: none;
  border-radius: 999px;
  padding: 10px 14px;
  font-weight: 900;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  background: ${({ $active }) => ($active ? "#0f766e" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#111827")};

  &:hover {
    background: ${({ $active }) => ($active ? "#0f766e" : "#f9fafb")};
  }
`;

const Body = styled.div`
  padding: 18px;
`;

const List = styled.div`
  display: grid;
  gap: 10px;

  @media (min-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #fafafa;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const RowToday = styled(Row)`
  background: rgba(15, 118, 110, 0.1);
  border-color: rgba(15, 118, 110, 0.25);
`;

const Day = styled.div`
  font-weight: 900;
  color: #111827;
`;

const Time = styled.div`
  font-weight: 900;
  color: #111827;
`;

const Badge = styled.span`
  margin-left: 8px;
  font-size: 12px;
  font-weight: 900;
  color: #0f766e;
  background: rgba(15, 118, 110, 0.15);
  border-radius: 999px;
  padding: 4px 10px;
`;

const Empty = styled.div`
  color: #6b7280;
  font-weight: 800;
`;

const Debug = styled.pre`
  margin-top: 14px;
  padding: 12px;
  border-radius: 14px;
  border: 1px dashed #e5e7eb;
  background: #fff;
  color: #111827;
  font-size: 12px;
  overflow: auto;
`;

/* ----------- Helpers ----------- */

const sanitize = (v) => String(v ?? "").trim().replace(/^"+|"+$/g, "");

const extractRows = (text = "") => {
  const cleaned = sanitize(text)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/\r/g, "")
    .trim();

  if (!cleaned) return [];

  const lines = cleaned
    .split("\n")
    .map((r) => r.trim())
    .filter(Boolean);

  const timeLike = /(\d{1,2}[:.]\d{2}|\d{1,2})\s*[–-]\s*(\d{1,2}[:.]\d{2}|\d{1,2})/; // 10–20, 10:00–18:30
  const dayWords = /(mån|tis|ons|tor|fre|lör|sön)/i;

  return lines
    .map((line) => {
      // 1) Om "Dag: Tid" eller "Dag - Tid"
      let m = line.match(/^(.+?)(?:\s*[:\-]\s*)(.+)$/);
      if (m) return { day: m[1].trim(), time: m[2].trim() };

      // 2) Om "Mån 10–20" (dag + tid med mellanslag)
      //    Vi hittar första "tid-liknande" del och splittar där.
      const idx = line.search(timeLike);
      if (idx > 0 && dayWords.test(line.slice(0, idx))) {
        const day = line.slice(0, idx).trim().replace(/\s+$/, "");
        const time = line.slice(idx).trim();
        if (day && time) return { day, time };
      }

      // 3) Om "Sön Stängt" (dag + text utan kolon)
      //    Splitta på första mellanslag efter dag-del
      const m2 = line.match(/^(.+?\b)(\s+)(.+)$/);
      if (m2 && dayWords.test(m2[1])) {
        return { day: m2[1].trim(), time: m2[3].trim() };
      }

      // 4) fallback: visa hela raden som "day", tom tid (om du vill)
      // return { day: line, time: "" };
      return null;
    })
    .filter(Boolean);
};


const isToday = (label) => {
  const today = new Date().getDay(); // 0–6
  const map = ["sön", "mån", "tis", "ons", "tor", "fre", "lör"];
  const t = map[today];

  const s = (label || "").toLowerCase();

  // direkt match (t.ex. "mån")
  if (s.includes(t)) return true;

  // intervall "mån–fre" eller "mån-fre"
  const m = s.match(/(mån|tis|ons|tor|fre|lör|sön)\s*[–-]\s*(mån|tis|ons|tor|fre|lör|sön)/);
  if (!m) return false;

  const order = ["mån", "tis", "ons", "tor", "fre", "lör", "sön"];
  const a = order.indexOf(m[1]);
  const b = order.indexOf(m[2]);
  const x = order.indexOf(t);

  if (a === -1 || b === -1 || x === -1) return false;
  if (a <= b) return x >= a && x <= b;        // mån–fre
  return x >= a || x <= b;                    // fre–mån (wrap)
};

/* ----------- Component ----------- */

const OpeningHoursSection = () => {
  const [data, setData] = useState({});
  const [active, setActive] = useState("folketsPark");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const snap = await getDoc(doc(firestore, "settings", "openingHours"));
        setData(snap.exists() ? snap.data() : {});
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const content = useMemo(() => {
    return active === "centrum" ? sanitize(data.centrum) : sanitize(data.folketsPark);
  }, [active, data]);

  const rows = useMemo(() => extractRows(content), [content]);
  const year = new Date().getFullYear();

  return (
    <Card>
      <Header>
        <Title>Öppettider {year}</Title>
      </Header>

      <FilterRow>
        <FilterBtn type="button" $active={active === "folketsPark"} onClick={() => setActive("folketsPark")}>
          Äventyrsgolf
        </FilterBtn>

        <FilterBtn type="button" $active={active === "centrum"} onClick={() => setActive("centrum")}>
          Bangolf
        </FilterBtn>
      </FilterRow>

      <Body>
        {loading ? (
          <Empty>Laddar…</Empty>
        ) : rows.length ? (
          <List>
            {rows.map((r, i) => {
              const TodayRow = isToday(r.day) ? RowToday : Row;
              return (
                <TodayRow key={i}>
                  <Day>{r.day}</Day>
                  <Time>
                    {r.time}
                    {isToday(r.day) && <Badge>Idag</Badge>}
                  </Time>
                </TodayRow>
              );
            })}
          </List>
        ) : (
          <Empty>
            Inga öppettider för <b>{active === "centrum" ? "Bangolf" : "Äventyrsgolf"}</b>.
            <br />
           
          </Empty>
        )}


      </Body>
    </Card>
  );
};

export default OpeningHoursSection;







